/**
 * Admin Settings
 * Global settings for diagnostic system
 */

import { createFileRoute } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Settings as SettingsIcon } from 'lucide-react';

export const Route = createFileRoute('/admin/diagnostic/settings')({
  component: AdminSettings,
});

function AdminSettings() {
  return (
    <div className="flex flex-col h-full">
    <div className="flex-1 overflow-y-auto">
    <div className="p-8 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Settings</h1>
        <p className="text-muted-foreground mt-1">
          Diagnostic system configuration
        </p>
      </div>

      <div className="space-y-6">
        {/* System Info */}
        <Card>
          <CardHeader>
            <CardTitle>System Information</CardTitle>
            <CardDescription>
              Current diagnostic system configuration
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between py-2 border-b">
              <span className="text-sm font-medium">Total Questions</span>
              <Badge variant="secondary">104</Badge>
            </div>

            <div className="flex items-center justify-between py-2 border-b">
              <span className="text-sm font-medium">Pillars</span>
              <Badge variant="secondary">6</Badge>
            </div>

            <div className="flex items-center justify-between py-2 border-b">
              <span className="text-sm font-medium">Lifecycle Stages</span>
              <Badge variant="secondary">10</Badge>
            </div>

            <div className="flex items-center justify-between py-2 border-b">
              <span className="text-sm font-medium">Guardrail Rules</span>
              <Badge variant="secondary">10</Badge>
            </div>

            <div className="flex items-center justify-between py-2">
              <span className="text-sm font-medium">Question Types</span>
              <Badge variant="secondary">5</Badge>
            </div>
          </CardContent>
        </Card>

        {/* Pillar Weights */}
        <Card>
          <CardHeader>
            <CardTitle>Pillar Weights</CardTitle>
            <CardDescription>
              Default weights for BSS calculation
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                { name: 'Finance', weight: 20 },
                { name: 'Operations', weight: 20 },
                { name: 'Management', weight: 20 },
                { name: 'Marketing', weight: 15 },
                { name: 'Sales', weight: 15 },
                { name: 'Culture', weight: 10 },
              ].map((pillar) => (
                <div key={pillar.name} className="flex items-center justify-between">
                  <span className="text-sm font-medium">{pillar.name}</span>
                  <div className="flex items-center gap-3">
                    <div className="w-48 h-2 bg-secondary rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary"
                        style={{ width: `${pillar.weight}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium w-12 text-right">
                      {pillar.weight}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Score Ranges */}
        <Card>
          <CardHeader>
            <CardTitle>Lifecycle Stage Ranges</CardTitle>
            <CardDescription>
              Prime Score ranges for each lifecycle stage
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {[
                { stage: 'Prime', range: '85-100', color: 'bg-green-500' },
                { stage: 'Systemise', range: '70-84', color: 'bg-blue-500' },
                { stage: 'Scale-up', range: '60-69', color: 'bg-yellow-500' },
                { stage: 'Buildup', range: '40-59', color: 'bg-orange-500' },
                { stage: 'Startup', range: '20-39', color: 'bg-red-500' },
                { stage: 'Birth', range: '10-19', color: 'bg-purple-500' },
                { stage: 'Death', range: '0-9', color: 'bg-gray-500' },
              ].map((item) => (
                <div key={item.stage} className="flex items-center gap-4 py-2">
                  <div className={`w-3 h-3 rounded-full ${item.color}`} />
                  <span className="text-sm font-medium w-24">{item.stage}</span>
                  <Badge variant="outline" className="font-mono">
                    {item.range}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Future Features */}
        <Card className="border-dashed">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <SettingsIcon className="w-5 h-5" />
              Future Configuration Options
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>• Edit pillar weights dynamically</li>
              <li>• Customize lifecycle stage score ranges</li>
              <li>• Configure trend modifier thresholds</li>
              <li>• Set up custom guardrail rules</li>
              <li>• Manage question versioning policies</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
    </div>
    </div>
  );
}
